import {noop} from 'instantsearch.js/es/lib/utils';
import {RenderOptions} from 'instantsearch.js/es/types';
import _isNil from 'lodash/isNil';

/**
 * The connector for text and image widget.
 * All the methods should be implemented as a property
 *
 * @todo Excluded refinement
 * @todo The options in the widget is a numeric refinement
 */
export default class TextAndImageConnector {
    constructor(widgetParams, renderCallback, unmountCallback) {
        this.widgetParams = widgetParams;
        this.renderCallback = renderCallback;
        this.unmountCallback = unmountCallback ?? noop;

        if (!/^(and|or)$/.test(this.operator)) {
            throw new Error(`The \`operator\` must one of: \`"and"\`, \`"or"\` (got "${this.operator}").`);
        }

        Object.defineProperty(this, '$$type', {
            value: 'intellishop.textAndImageRefinementList',
            writable: false,
            configurable: false
        })
    }

    get attribute() {
        return this.widgetParams.attribute;
    }

    get answers() {
        return this.widgetParams.answers;
    }

    get operator() {
        return this.widgetParams.operator || 'or';
    }

    _getRefinementValue(searchParameters) {
        if (searchParameters.isConjunctiveFacet(this.attribute)) {
            return  searchParameters.getConjunctiveRefinements(this.attribute)[0];
        } else if (searchParameters.isDisjunctiveFacet(this.attribute)) {
            return  searchParameters.getDisjunctiveRefinements(this.attribute)[0];
        }

        return undefined;
    }

    init = (initOptions) => {
        this.renderCallback(
            {
                ...this.getWidgetRenderState(initOptions),
                instantSearchInstance: initOptions.instantSearchInstance,
            },
            true
        );
    };

    /**
     *
     * @param {RenderOptions} renderOptions
     */
    render = (renderOptions)=> {
        this.renderCallback(
            {
                ...this.getWidgetRenderState(renderOptions),
                instantSearchInstance: renderOptions.instantSearchInstance,
            },
            false
        );
    };

    getRenderState = (renderState, renderOptions) => {
        return {
            ...renderState,
            [this.attribute]: this.getWidgetRenderState(renderOptions)
        };
    };

    getWidgetRenderState = (renderOptions) => {
        let facetValues = (renderOptions.results ? renderOptions.results.getFacetValues(this.attribute) : []),
            items= this.answers.reduce((facetItems, answerItem) => {
                if (facetValues.find(facetItem => facetItem.escapedValue === (answerItem.id + ''))) {
                    return [...facetItems, answerItem];
                }

                return facetItems;
            }, []),
            refine = (value) => {
                let valueAsString = value + '',     // cast the value to string, because the value is cast to string and in the refinement
                    {helper} = renderOptions,
                    isRefinementExisted = !!helper.getRefinements(this.attribute).find(refinement => refinement.value === valueAsString);

                helper.clearRefinements(this.attribute)

                if (!isRefinementExisted) {
                    if (this.operator === 'or') {
                        helper.addDisjunctiveFacetRefinement(this.attribute, valueAsString);
                    } else {
                        helper.addFacetRefinement(this.attribute, valueAsString);
                    }
                }

                helper.search();
            };

        return {
            items,
            refine,
            activeItem: this._getRefinementValue(renderOptions.state),
            widgetParams: this.widgetParams,
        };

    };

    dispose = ({state})=> {
        this.unmountCallback();
    };

    getWidgetUiState = (uiState, options) => {
        let value = this._getRefinementValue(options.searchParameters);

        if (_isNil(value)) {
            // Make sure the attribute entry is removed from the list
            let {[this.attribute]:attribute, ...nextUiState} = uiState;

            return nextUiState;
        } else {
            return  {
                ...uiState,
                [this.attribute]: value,
            };
        }
    };

    getWidgetSearchParameters = (searchParameters, {uiState}) => {
        // Make sure the attribute is added without refinement value
        let nextSearchParameters = this.operator === 'or' ?
            searchParameters.addDisjunctiveFacet(this.attribute):
            searchParameters.addFacet(this.attribute);

        nextSearchParameters = nextSearchParameters.clearRefinements(this.attribute);

        if (uiState.hasOwnProperty(this.attribute)) {
            return this.operator === 'or' ?
                nextSearchParameters.addDisjunctiveFacetRefinement(this.attribute, uiState[this.attribute]):
                nextSearchParameters.addFacetRefinement(this.attribute, uiState[this.attribute]);
        }

        return nextSearchParameters;
    };
}